export const HISTORY_LINE_COLORS = [
  "rgba(121,166,255,0.92)",
  "rgba(75,211,123,0.88)",
  "rgba(255,171,64,0.90)",
  "rgba(196,132,255,0.90)",
  "rgba(255,95,109,0.90)",
  "rgba(86,233,233,0.85)",
  "rgba(255,255,255,0.72)",
]

export function timeMsForForecast(forecast) {
  const raw = forecast?.completed_at || forecast?.created_at
  if (!raw) return null
  const ms = new Date(raw).getTime()
  return Number.isFinite(ms) ? ms : null
}

function startOfDay(ms) {
  const d = new Date(ms)
  d.setHours(0, 0, 0, 0)
  return d
}

function startOfMonth(ms) {
  const d = new Date(ms)
  d.setDate(1)
  d.setHours(0, 0, 0, 0)
  return d
}

function startOfYear(ms) {
  const d = new Date(ms)
  d.setMonth(0, 1)
  d.setHours(0, 0, 0, 0)
  return d
}

function addInterval(date, unit, step) {
  const next = new Date(date.getTime())
  if (unit === "year") {
    next.setFullYear(next.getFullYear() + step)
    return next
  }
  if (unit === "month") {
    next.setMonth(next.getMonth() + step)
    return next
  }
  next.setDate(next.getDate() + step)
  return next
}

function alignBoundary(ms, unit) {
  if (unit === "year") return startOfYear(ms)
  if (unit === "month") return startOfMonth(ms)
  return startOfDay(ms)
}

function chooseAxisUnit(rangeMs) {
  const dayMs = 24 * 60 * 60 * 1000
  if (rangeMs >= 730 * dayMs) return "year"
  if (rangeMs >= 120 * dayMs) return "month"
  return "day"
}

function estimateIntervalCount(xMin, xMax, unit) {
  const start = alignBoundary(xMin, unit)
  let current = start
  let count = 0
  while (current.getTime() <= xMax) {
    current = addInterval(current, unit, 1)
    count += 1
    if (count > 2000) break
  }
  return Math.max(1, count)
}

function chooseAxisStep(unit, width, intervalCount) {
  const minLabelPx = unit === "day" ? 72 : unit === "month" ? 84 : 72
  const maxLabels = Math.max(1, Math.floor((width || 0) / minLabelPx))
  return Math.max(1, Math.ceil(intervalCount / Math.max(1, maxLabels)))
}

function formatIntervalLabel(ms, unit, width, step) {
  const d = new Date(ms)
  if (Number.isNaN(d.getTime())) return { primary: "—", secondary: null, multiline: false }
  if (unit === "year") return { primary: d.toLocaleDateString(undefined, { year: "numeric" }), secondary: null, multiline: false }
  if (unit === "month") {
    const month = d.toLocaleDateString(undefined, { month: "short" })
    const year = d.toLocaleDateString(undefined, { year: "numeric" })
    const multiline = (width || 0) < 720 || step === 1
    return { primary: month, secondary: year, multiline }
  }
  return { primary: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }), secondary: null, multiline: false }
}

export function buildHistoryTimeAxis(xValues, width) {
  if (!Array.isArray(xValues) || xValues.length === 0) {
    return { tickValues: [], labelValues: [], unit: "day", step: 1 }
  }

  const xMin = xValues[0]
  const xMax = xValues[xValues.length - 1]
  const rangeMs = Math.max(0, xMax - xMin)
  const unit = chooseAxisUnit(rangeMs)
  const intervalCount = estimateIntervalCount(xMin, xMax, unit)
  const step = chooseAxisStep(unit, width, intervalCount)

  const firstBoundary = alignBoundary(xMin, unit)
  const tickValues = []
  const labelValues = []

  let boundary = firstBoundary
  let guard = 0
  while (boundary.getTime() <= xMax) {
    tickValues.push(boundary.getTime())
    boundary = addInterval(boundary, unit, step)
    guard += 1
    if (guard > 2000) break
  }
  tickValues.push(boundary.getTime())

  for (let i = 0; i < tickValues.length - 1; i += 1) {
    const start = tickValues[i]
    const end = tickValues[i + 1]
    if (end < xMin || start > xMax) continue
    labelValues.push({
      x: start + (end - start) / 2,
      ...formatIntervalLabel(start, unit, width, step),
    })
  }

  return { tickValues, labelValues, unit, step }
}

export function buildLinePath(points, xFor, yFor) {
  let d = ""
  let started = false
  for (const pt of points) {
    if (pt.y == null || !Number.isFinite(pt.y)) {
      started = false
      continue
    }
    const x = xFor(pt.x)
    const y = yFor(pt.y)
    d += `${started ? " L" : "M"} ${x.toFixed(2)} ${y.toFixed(2)}`
    started = true
  }
  return d.trim()
}
